// src/app/data/navigation-utils.ts

import type { NavigationLink, NavigationCategory, NavigationGroup, NavigationMetadata, SubdomainType, ScreenSize } from './navigation-types.ts';
import { navigationCategories } from './navigation-data.ts';
import { navigationMetadata } from './navigation-constants.ts';
import { performanceMonitor } from '../../lib/performance.ts';

// Enhanced cache with timestamps for better cache invalidation
interface CacheEntry<T> {
  data: T;
  timestamp: number;
  ttl: number; // Time to live in milliseconds
}

class NavigationCache {
  private cache = new Map<string, CacheEntry<any>>();
  private readonly DEFAULT_TTL = 5 * 60 * 1000; // 5 minutes

  set<T>(key: string, data: T, ttl = this.DEFAULT_TTL): void {
    this.cache.set(key, {
      data,
      timestamp: Date.now(),
      ttl,
    });
  }

  get<T>(key: string): T | null {
    const entry = this.cache.get(key);
    if (!entry) {
      performanceMonitor.recordCacheMiss();
      return null;
    }

    // Check if cache entry has expired
    if (Date.now() - entry.timestamp > entry.ttl) {
      this.cache.delete(key);
      performanceMonitor.recordCacheMiss();
      return null;
    }

    performanceMonitor.recordCacheHit();
    return entry.data as T;
  }

  clear(): void {
    this.cache.clear();
  }

  // Clear expired entries
  cleanup(): void {
    const now = Date.now();
    const keysToDelete: string[] = [];

    // Use Array.from to convert iterator to array
    Array.from(this.cache.entries()).forEach(([key, entry]) => {
      if (now - entry.timestamp > entry.ttl) {
        keysToDelete.push(key);
      }
    });

    keysToDelete.forEach(key => this.cache.delete(key));
  }
}

// Global navigation cache instance
const navigationCache = new NavigationCache();

/**
 * Get all navigation links with enhanced search capabilities and caching
 */
export function getAllNavigationLinks(): NavigationLink[] {
  const cacheKey = 'all-navigation-links';

  // Try to get from cache first
  const cached = navigationCache.get<NavigationLink[]>(cacheKey);
  if (cached) {
    return cached;
  }

  // Compute navigation links
  const links: NavigationLink[] = [
    { name: "Home", url: "/", description: "Homepage", keywords: ["home", "main", "index"] },
    ...navigationCategories.flatMap(category =>
      category.items.map(item => ({ ...item, category: category.name }))
    )
  ];

  // Cache the result
  navigationCache.set(cacheKey, links);
  return links;
}

/**
 * Get navigation links by category with caching
 */
export function getLinksByCategory(categoryName: string): NavigationLink[] {
  const cacheKey = `category-links-${categoryName}`;

  // Try to get from cache first
  const cached = navigationCache.get<NavigationLink[]>(cacheKey);
  if (cached) {
    return cached;
  }

  // Find and cache the category
  const category = navigationCategories.find(cat => cat.name === categoryName);
  const links = category ? category.items : [];

  navigationCache.set(cacheKey, links);
  return links;
}

/**
 * Search navigation links by keyword with caching
 */
export function searchNavigationLinks(query: string): NavigationLink[] {
  const cacheKey = `search-${query.toLowerCase()}`;

  // Try to get from cache first
  const cached = navigationCache.get<NavigationLink[]>(cacheKey);
  if (cached) {
    return cached;
  }

  // Perform search
  const allLinks = getAllNavigationLinks();
  const lowercaseQuery = query.toLowerCase();

  const results = allLinks.filter(link =>
    link.name.toLowerCase().includes(lowercaseQuery) ||
    link.description?.toLowerCase().includes(lowercaseQuery) ||
    link.keywords?.some(keyword => keyword.toLowerCase().includes(lowercaseQuery))
  );

  // Cache the search results (with shorter TTL for search queries)
  navigationCache.set(cacheKey, results, 2 * 60 * 1000); // 2 minutes for search results
  return results;
}

/**
 * Get popular navigation links with caching
 */
export function getPopularLinks(): NavigationLink[] {
  const cacheKey = 'popular-links';

  const cached = navigationCache.get<NavigationLink[]>(cacheKey);
  if (cached) {
    return cached;
  }

  const links = getAllNavigationLinks().filter(link => link.isPopular);
  navigationCache.set(cacheKey, links);
  return links;
}

/**
 * Get recently added navigation links with caching
 */
export function getNewLinks(): NavigationLink[] {
  const cacheKey = 'new-links';

  const cached = navigationCache.get<NavigationLink[]>(cacheKey);
  if (cached) {
    return cached;
  }

  const links = getAllNavigationLinks().filter(link => link.isNew);
  navigationCache.set(cacheKey, links);
  return links;
}

/**
 * Get subdomain-specific navigation links
 */
export function getSubdomainLinks(subdomain: 'notes' | 'auth' | 'learn'): NavigationLink[] {
  switch (subdomain) {
    case 'learn':
      return [
        { name: "Quiz builder", url: "/learn", description: "Create quizzes from text or manually", keywords: ["quiz", "learn", "study"] },
        { name: "Back to EYN", url: "https://eyn.tools", description: "Return to main site", keywords: ["main", "site", "home"], isExternal: true },
      ];
    case 'notes':
      return [
        { name: "Notes Dashboard", url: "/notes", description: "Your notes dashboard", keywords: ["notes", "dashboard", "home"] },
        { name: "Notebooks", url: "/notebooks", description: "Manage your notebooks", keywords: ["notebooks", "organize", "folders"] },
        { name: "Search", url: "/search", description: "Search your notes", keywords: ["search", "find", "notes"] },
        { name: "Tags", url: "/tags", description: "Manage note tags", keywords: ["tags", "categorize", "organize"] },
        { name: "Back to EYN", url: "https://eyn.tools", description: "Return to main site", keywords: ["main", "site", "home"], isExternal: true },
      ];
    case 'auth':
      return [
        { name: "Login", url: "/auth/login", description: "Sign in to your account", keywords: ["login", "signin", "auth"] },
        { name: "Sign Up", url: "/auth/signup", description: "Create a new account", keywords: ["signup", "register", "auth"] },
        { name: "Login (External)", url: "https://auth.eyn.tools/auth/login", description: "Sign in to your account", keywords: ["login", "signin", "auth"], isExternal: true },
        { name: "Sign Up (External)", url: "https://auth.eyn.tools/auth/signup", description: "Create a new account", keywords: ["signup", "register", "auth"], isExternal: true },
        { name: "Pricing", url: "/pricing", description: "View pricing plans", keywords: ["pricing", "plans", "subscription"] },
        { name: "Forgot Password", url: "/auth/forgot-password", description: "Reset your password", keywords: ["password", "reset", "forgot"] },
        { name: "Back to EYN", url: "https://eyn.tools", description: "Return to main site", keywords: ["main", "site", "home"], isExternal: true },
      ];
    default:
      return [];
  }
}

/**
 * Get external subdomain links
 */
export function getExternalSubdomainLinks(): NavigationLink[] {
  return [
    { name: "Learn", url: "https://learn.eyn.tools", description: "Build quizzes from text or by hand", keywords: ["quiz", "learn", "study"], isExternal: true },
    { name: "Notes", url: "https://notes.eyn.tools", description: "Organize your thoughts and ideas", keywords: ["notes", "notebook", "organize"], isExternal: true },
    { name: "Authentication", url: "https://auth.eyn.tools", description: "Sign in or create an account", keywords: ["auth", "login", "signup"], isExternal: true },
  ];
}

/**
 * Detect current subdomain context
 */
export function getCurrentSubdomainContext(hostname?: string): SubdomainType {
  if (typeof window === 'undefined' && !hostname) return 'unknown';

  const currentHostname = hostname || (typeof window !== 'undefined' ? window.location.hostname : '');

  if (currentHostname.startsWith('notes.') || currentHostname.includes('.notes.')) {
    return 'notes';
  }

  if (currentHostname.startsWith('auth.') || currentHostname.includes('.auth.')) {
    return 'auth';
  }

  if (currentHostname.startsWith('learn.') || currentHostname.includes('.learn.')) {
    return 'learn';
  }

  if (currentHostname.includes('eyn.tools') || currentHostname.includes('localhost')) {
    return 'main';
  }

  return 'unknown';
}

/**
 * Get navigation links based on current context
 */
export function getContextualNavigationLinks(hostname?: string): NavigationLink[] {
  const context = getCurrentSubdomainContext(hostname);

  switch (context) {
    case 'notes':
      return getSubdomainLinks('notes');
    case 'auth':
      return getSubdomainLinks('auth');
    case 'learn':
      return getSubdomainLinks('learn');
    case 'main':
      return getAllNavigationLinks();
    default:
      return getAllNavigationLinks();
  }
}

/**
 * Get visible navigation categories (sorted by priority)
 */
export function getVisibleCategories(): NavigationCategory[] {
  return navigationCategories
    .filter(category => category.isVisible !== false)
    .sort((a, b) => (a.priority ?? 999) - (b.priority ?? 999));
}

/**
 * Get navigation categories for different screen sizes
 */
export function getCategoriesForScreenSize(screenSize: ScreenSize): NavigationCategory[] {
  const visibleCategories = getVisibleCategories();

  switch (screenSize) {
    case 'mobile':
      return visibleCategories; // Show all on mobile
    case 'tablet':
      return visibleCategories.slice(0, 4); // Show first 4 on tablet
    case 'desktop':
      return visibleCategories.slice(0, 6); // Show first 6 on desktop
    default:
      return visibleCategories;
  }
}

/**
 * Validate navigation structure
 */
export function validateNavigation(): { isValid: boolean; errors: string[] } {
  const errors: string[] = [];

  // Check for duplicate URLs
  const allLinks = getAllNavigationLinks();
  const urls = allLinks.map(link => link.url);
  const duplicateUrls = urls.filter((url, index) => urls.indexOf(url) !== index);

  if (duplicateUrls.length > 0) {
    errors.push(`Duplicate URLs found: ${duplicateUrls.join(', ')}`);
  }

  // Check for empty categories
  const emptyCategories = navigationCategories.filter(cat => cat.items.length === 0);
  if (emptyCategories.length > 0) {
    errors.push(`Empty categories found: ${emptyCategories.map(cat => cat.name).join(', ')}`);
  }

  return {
    isValid: errors.length === 0,
    errors
  };
}

/**
 * Get main navigation links (memoized with new cache system)
 */
export function getMainNavLinks(): NavigationLink[] {
  const cacheKey = 'main-nav-links';

  const cached = navigationCache.get<NavigationLink[]>(cacheKey);
  if (cached) {
    return cached;
  }

  const links = getAllNavigationLinks();
  navigationCache.set(cacheKey, links);
  return links;
}

/**
 * Get resource links (memoized with new cache system)
 */
export function getResourceLinks(): NavigationLink[] {
  const cacheKey = 'resource-links';

  const cached = navigationCache.get<NavigationLink[]>(cacheKey);
  if (cached) {
    return cached;
  }

  const links = [
    { name: "Blog", url: "/blog", description: "Latest blog posts", keywords: ["blog", "posts", "articles"] },
    { name: "Portfolio", url: "/projects", description: "Project portfolio", keywords: ["portfolio", "projects", "work"] },
    { name: "Resume", url: "/resume", isExternal: true, description: "Download resume", keywords: ["resume", "cv", "download"] },
  ];

  navigationCache.set(cacheKey, links);
  return links;
}

/**
 * Get footer link groups (memoized with new cache system)
 */
export function getFooterLinkGroups(): NavigationGroup[] {
  const cacheKey = 'footer-link-groups';

  const cached = navigationCache.get<NavigationGroup[]>(cacheKey);
  if (cached) {
    return cached;
  }

  const groups = [
    {
      title: "Navigation",
      links: getMainNavLinks(),
    },
    {
      title: "Resources",
      links: getResourceLinks(),
    },
  ];

  navigationCache.set(cacheKey, groups);
  return groups;
}

// Cache management functions
export function clearNavigationCache(): void {
  navigationCache.clear();
}

export function cleanupNavigationCache(): void {
  navigationCache.cleanup();
}

export function reportNavigationPerformance(): void {
  performanceMonitor.logMetrics('Navigation System');
}

// Export cache instance for advanced cache management
export { navigationCache };

// Legacy exports for backward compatibility
export const mainNavLinks = getMainNavLinks();
export const resourceLinks = getResourceLinks();
export const footerLinkGroups = getFooterLinkGroups();
